import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "className", "component", "disabled", "href"];
import React from 'react';
import PropTypes from 'prop-types';
import { default as withStyles } from '../styles';
import classNames from 'classnames';
import { alpha } from '@mui/system';
import { fontFamilyDefault, lineHeightDefault, borderRadiusSmall, borderWidthThick, colorBorderGlobalFocus, spacing10 } from '../styles/tokens';
var styles = function styles(theme) {
  return {
    link: {
      fontFamily: fontFamilyDefault,
      lineHeight: lineHeightDefault,
      textDecoration: 'underline',
      color: theme.palette.action.base,
      // cta blue 600
      textDecorationColor: alpha(theme.palette.action.base, .4),
      transition: theme.transitions.create(['text-decoration-color', 'color'], {
        duration: theme.transitions.duration.shortest
      }),
      '&:hover': {
        color: theme.palette.action.active,
        // cta blue 800
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationColor: theme.palette.action.active
      },
      '&:active': {
        color: theme.palette.text.primary,
        // #151618
        textDecoration: 'underline'
      },
      '&:focus': {
        outline: borderWidthThick + " solid " + colorBorderGlobalFocus,
        outlineOffset: "" + spacing10,
        textDecoration: 'underline',
        borderRadius: borderRadiusSmall
      }
    },
    disabled: {
      pointerEvents: 'none',
      color: theme.palette.action.disabled,
      textDecorationColor: alpha(theme.palette.action.disabled, 1)
    }
  };
};

/**
 * Use `TextLink` to take the user to a new page.
 * @done true
 * @updated v7.4.0
 * @versionAdded v0.0.10
 * @examples
 *  TextLinkExample
 *  CustomComponent
 *  TextLinkTarget
 *  TextLinkAccessibility
 */
var TextLink = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;
  var classes = props.classes,
    className = props.className,
    Component = props.component,
    disabled = props.disabled,
    href = props.href,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var additionalProps = disabled ? {
    'aria-disabled': 'true'
  } : {
    href: href,
    ref: ref
  };
  var classOverrides = {
    stateStyle: classNames(className, classes.link, (_classNames = {}, _classNames[classes.disabled] = disabled, _classNames))
  };
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: classOverrides.stateStyle
  }, rest, additionalProps));
});
TextLink.muiName = 'TextLink';
TextLink.displayName = 'TextLink';
TextLink.propTypes = {
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * Custom className for additional styling
   */
  className: PropTypes.string,
  /**
   * The component used for the root node. We recommend using a component or element
   * that supports the `href` prop.
   *
   * If you use an element that does not natively have a `tabIndex`, be sure to add a `tabIndex`
   * to make sure the component is keyboard-navigable.
   */
  component: PropTypes.elementType,
  /**
   * Set to `true` to disable the link
   */
  disabled: PropTypes.bool,
  /**
   * The content of the `TextLink`.
   */
  children: PropTypes.node.isRequired,
  /**
   * The redirect URL. This prop is required if replacement `component` prop is not specified.
   */
  href: PropTypes.string,
  /**
   * Callback triggered when link is clicked.
   */
  onClick: PropTypes.func,
  /**
   * Where to display the linked URL, as the name for a browsing context (a tab, window, or `<iframe>`).
   */
  target: PropTypes.string

  // TODO: commenting this out for now — the underlying anchor element does not support the sx prop
  // sx: PropTypes.oneOfType([
  //     PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
  //     PropTypes.func,
  //     PropTypes.object,
  // ]),
};

TextLink.defaultProps = {
  component: 'a',
  disabled: false
};
export default withStyles(styles)(TextLink);